/**
 * DailyRoll — LOCKED CONTRACT: the one canonical pseudo-random generator.
 *
 * This is part of the determinism contract, not an implementation detail.
 * Course generation is the ONLY place randomness is allowed, and it MUST draw
 * exclusively from this generator. The live simulation MUST be RNG-free.
 *
 * Both the client (Core) and the server (Worker verification) import this exact
 * function. If they diverge, server-side replay verification breaks. Do not
 * swap the algorithm without bumping SIM_VERSION.
 *
 * Algorithm: mulberry32 — small, fast, fully deterministic 32-bit generator.
 *
 * Owned by: Architect. Used by: Core (generation), Backend (verification).
 */

export interface Rng {
  /** Next float in [0, 1). */
  next(): number;
  /** Next integer in [0, maxExclusive). */
  int(maxExclusive: number): number;
  /** Next float in [min, max). */
  range(min: number, max: number): number;
  /** Uniformly pick an element. Throws on empty input. */
  pick<T>(items: readonly T[]): T;
}

/** Create a deterministic generator from a 32-bit unsigned seed. */
export function makeRng(seed: number): Rng {
  // Force unsigned 32-bit state.
  let state = seed >>> 0;

  const next = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const int = (maxExclusive: number): number => Math.floor(next() * maxExclusive);
  const range = (min: number, max: number): number => min + next() * (max - min);
  const pick = <T>(items: readonly T[]): T => {
    if (items.length === 0) throw new Error('Rng.pick: empty array');
    return items[int(items.length)];
  };

  return { next, int, range, pick };
}
